import React, { useEffect, useRef, useState } from 'react';

// Renders its children only once the placeholder comes within `margin` of the
// screen, so a lazy chunk below the fold is not fetched while the first
// screen is still loading (decision A, 2026-09-29: the gallery must not move
// the hero's paint). No IntersectionObserver (an old browser, jsdom) renders
// at once: absence of the API is never absence of the content.
// `afterLoad`: also render once the page has finished loading and the main
// thread is idle (/tour: a visitor who scrolls fast, prints, or saves the page
// still gets every screen; the first screen has already painted by then).
export default function NearViewport({ children, margin = '600px', minHeight = 320, className = '', afterLoad = false }) {
    const ref = useRef(null);
    // The build's prerender renders the placeholder, like a browser's first
    // render, so the page hydrates without a mismatch (#2605); the browser
    // then loads the content as it nears the screen.
    const [near, setNear] = useState(() => !import.meta.env.SSR && typeof IntersectionObserver !== 'function');
    useEffect(() => {
        if (near || !ref.current) return undefined;
        const io = new IntersectionObserver((entries) => {
            if (entries.some((e) => e.isIntersecting)) { setNear(true); io.disconnect(); }
        }, { rootMargin: `${margin} 0px` });
        io.observe(ref.current);
        return () => io.disconnect();
    }, [near, margin]);
    useEffect(() => {
        if (near || !afterLoad || typeof window === 'undefined') return undefined;
        let timer;
        const go = () => { timer = setTimeout(() => setNear(true), 1200); };
        if (document.readyState === 'complete') go();
        else window.addEventListener('load', go, { once: true });
        return () => { clearTimeout(timer); window.removeEventListener('load', go); };
    }, [near, afterLoad]);
    return (
        <div ref={ref} className={className} style={near ? undefined : { minHeight }}>
            {near ? children : null}
        </div>
    );
}
